"use client";

import { useCallback, useState } from "react";
import {
  CheckCircle2,
  ClipboardList,
  FileText,
  Loader2,
  MessageSquare,
  Sparkles,
  Users,
} from "lucide-react";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useBrands } from "@/hooks/use-brands";
import { toast } from "sonner";
import { CelebrationConfetti } from "../../shared/celebration-confetti";

export function WorkspaceDoneStep() {
  const { brands } = useBrands();
  const defaultBrand =
    brands.find((brand) => brand.isDefault) ?? brands[0] ?? null;
  const defaultBrandName = defaultBrand?.name?.trim() || "your default brand";

  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [addedQuoteCount, setAddedQuoteCount] = useState<number | null>(null);

  const { doAction: seedExampleData, isProcessing: isSeedingExampleData } =
    useSDKActionWithDeps(
      () => ({
        action: (sdk) => () => sdk.seedOrganizationExampleData(),
        failureMessage: "Failed to create example data",
        throwError: true,
      }),
      []
    );

  const handleSeedExampleData = useCallback(async () => {
    try {
      const result = await seedExampleData();
      setSeedDialogOpen(false);

      if (!result) return;

      const createdCount = result.contacts.length;
      const skippedCount = result.skipped.length;

      if (createdCount > 0 && skippedCount > 0) {
        setAddedQuoteCount(result.quotes.length || 3);
        toast.success(
          `Created ${createdCount} example set${createdCount === 1 ? "" : "s"}. Skipped ${skippedCount} that already exist.`
        );
        return;
      }

      if (createdCount > 0) {
        setAddedQuoteCount(result.quotes.length || 3);
        toast.success(
          `Created ${createdCount} example customers, enquiries, and quotes`
        );
        return;
      }

      if (skippedCount > 0) {
        setAddedQuoteCount(3);
      }

      toast.message("Example data already exists — nothing new was added");
    } catch {
      // toast handled by hook
    }
  }, [seedExampleData]);

  return (
    <>
      <CelebrationConfetti />

      <div className="space-y-5">
        <div className="relative overflow-hidden rounded-xl border border-green-200/80 bg-gradient-to-br from-green-50 via-background to-emerald-50/40 p-5 shadow-sm">
          <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-green-200/30 blur-2xl" />
          <div className="relative flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold tracking-tight">
                You&apos;re ready to go
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Core workspace setup is complete. You can start working in TA Desk
                right away.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-dashed bg-muted/20 px-4 py-3">
          <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Finish later in Manage Workspace</p>
            <p className="text-sm text-muted-foreground">
              Suppliers, payments, brand settings, and other checklist items can
              be updated anytime from workspace settings.
            </p>
          </div>
        </div>

        <div
          className={
            addedQuoteCount
              ? "overflow-hidden rounded-xl border border-green-200/80 bg-gradient-to-br from-green-50/80 via-background to-emerald-50/30 shadow-sm"
              : "overflow-hidden rounded-xl border bg-card shadow-sm"
          }
        >
          <div
            className={
              addedQuoteCount
                ? "px-5 py-4"
                : "border-b bg-muted/20 px-5 py-4"
            }
          >
            <div className="flex items-start gap-3">
              <div
                className={
                  addedQuoteCount
                    ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700"
                    : "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600"
                }
              >
                {addedQuoteCount ? (
                  <CheckCircle2 className="h-5 w-5" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </div>
              <div className="space-y-1">
                <p className="font-medium">
                  {addedQuoteCount
                    ? `${addedQuoteCount} example quote${addedQuoteCount === 1 ? "" : "s"} have been added`
                    : "Example quotes to get started"}
                </p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {addedQuoteCount
                    ? `Your example customers, enquiries, and quotes are ready on ${defaultBrandName}. Open Quotes or Enquiries to explore them.`
                    : `Add example customers, enquiries, and quotes so you can explore TA Desk right away. Records are created under your account on ${defaultBrandName}.`}
                </p>
              </div>
            </div>
          </div>

          {!addedQuoteCount ? (
            <>
              <div className="grid gap-3 px-5 py-4 sm:grid-cols-3">
                <div className="rounded-lg border bg-muted/10 p-3">
                  <Users className="mb-2 h-4 w-4 text-blue-600" />
                  <p className="text-sm font-medium">3 customers</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sample contacts to work with
                  </p>
                </div>
                <div className="rounded-lg border bg-muted/10 p-3">
                  <MessageSquare className="mb-2 h-4 w-4 text-violet-600" />
                  <p className="text-sm font-medium">3 enquiries</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Ready-made sales opportunities
                  </p>
                </div>
                <div className="rounded-lg border bg-muted/10 p-3">
                  <FileText className="mb-2 h-4 w-4 text-emerald-600" />
                  <p className="text-sm font-medium">3 quotes</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Example itineraries to explore
                  </p>
                </div>
              </div>

              <div className="border-t bg-muted/10 px-5 py-4">
                <Button
                  type="button"
                  className="w-full sm:w-auto"
                  onClick={() => setSeedDialogOpen(true)}
                  disabled={isSeedingExampleData}
                >
                  {isSeedingExampleData ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  Add example quotes
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <AlertDialog open={seedDialogOpen} onOpenChange={setSeedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add example quotes?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates 3 customers, 3 enquiries, and 3 quotes inspired by
              real example itineraries on {defaultBrandName}. They will appear as
              if you created them yourself and can be edited or deleted later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSeedingExampleData}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleSeedExampleData();
              }}
              disabled={isSeedingExampleData}
            >
              {isSeedingExampleData ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Add example quotes"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
